import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Bell, Shield, Bot, Save, Loader2, Share2, Facebook, Instagram, Youtube, Globe, CheckCircle2, AlertCircle, Crown, CreditCard, Sparkles, Zap } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CurrencySwitcher from '../components/CurrencySwitcher';

// TikTok and Pinterest icons
const TikTokIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PinterestIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.43-6.09s-.36-.73-.36-1.82c0-1.7.99-2.98 2.22-2.98 1.05 0 1.55.78 1.55 1.72 0 1.05-.67 2.62-1.01 4.07-.29 1.21.61 2.2 1.8 2.2 2.16 0 3.82-2.28 3.82-5.57 0-2.91-2.09-4.95-5.08-4.95-3.46 0-5.49 2.6-5.49 5.28 0 1.05.4 2.17.91 2.78.1.12.11.23.08.35l-.34 1.38c-.05.22-.18.27-.42.16-1.56-.73-2.54-3-2.54-4.83 0-3.93 2.85-7.54 8.24-7.54 4.32 0 7.68 3.08 7.68 7.19 0 4.3-2.71 7.76-6.47 7.76-1.26 0-2.45-.66-2.86-1.43l-.78 2.96c-.28 1.08-1.04 2.44-1.55 3.27A12 12 0 1 0 12 0z"/>
  </svg>
);

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Profile settings
  const [profile, setProfile] = useState({
    display_name: '',
    bio: '',
    avatar: '',
    links: [],
    country: '',
    website: ''
  });
  
  // Social links
  const [socialLinks, setSocialLinks] = useState({
    social_facebook: '',
    social_instagram: '',
    social_tiktok: '',
    social_youtube: '',
    social_pinterest: ''
  });
  
  // Social link validation errors
  const [socialErrors, setSocialErrors] = useState({});
  
  // App settings
  const [settings, setSettings] = useState({
    notification_email: true,
    notification_push: true,
    notification_messages: true,
    notification_likes: true,
    notification_comments: true,
    privacy_profile_visible: true,
    privacy_online_status: true,
    ai_reply_mode: 'off',
    ai_auto_reply_message: ''
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const [profileRes, settingsRes] = await Promise.all([
        api.get(`/users/${user.id}`),
        api.get('/settings')
      ]);
      setProfile({
        display_name: profileRes.data.display_name || '',
        bio: profileRes.data.bio || '',
        avatar: profileRes.data.avatar || '',
        links: profileRes.data.links || [],
        country: profileRes.data.country || '',
        website: profileRes.data.website || ''
      });
      setSocialLinks({
        social_facebook: profileRes.data.social_facebook || '',
        social_instagram: profileRes.data.social_instagram || '',
        social_tiktok: profileRes.data.social_tiktok || '',
        social_youtube: profileRes.data.social_youtube || '',
        social_pinterest: profileRes.data.social_pinterest || ''
      });
      setSettings({
        notification_email: settingsRes.data.notification_email ?? true,
        notification_push: settingsRes.data.notification_push ?? true,
        notification_messages: settingsRes.data.notification_messages ?? true,
        notification_likes: settingsRes.data.notification_likes ?? true,
        notification_comments: settingsRes.data.notification_comments ?? true,
        privacy_profile_visible: settingsRes.data.privacy_profile_visible ?? true,
        privacy_online_status: settingsRes.data.privacy_online_status ?? true,
        ai_reply_mode: settingsRes.data.ai_reply_mode || 'off',
        ai_auto_reply_message: settingsRes.data.ai_auto_reply_message || 'Thanks for your message! I\'m currently away but will get back to you soon.'
      });
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      await api.put(`/users/${user.id}`, profile);
      if (refreshUser) await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Validate URL format
  const validateUrl = (url, platform) => {
    if (!url) return true; // Empty is valid
    
    // Basic URL pattern
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;
    if (!urlPattern.test(url)) {
      return false;
    }
    
    // Platform-specific validation
    const platformPatterns = {
      social_facebook: /facebook\.com|fb\.com/i,
      social_instagram: /instagram\.com/i,
      social_tiktok: /tiktok\.com/i,
      social_youtube: /youtube\.com|youtu\.be/i,
      social_pinterest: /pinterest\.com|pin\.it/i
    };
    
    if (platformPatterns[platform] && !platformPatterns[platform].test(url)) {
      return false;
    }
    
    return true;
  };

  const handleSocialLinkChange = (platform, value) => {
    setSocialLinks(prev => ({ ...prev, [platform]: value }));
    
    // Clear error when user starts typing
    if (socialErrors[platform]) {
      setSocialErrors(prev => ({ ...prev, [platform]: null }));
    }
  };

  const saveSocialLinks = async () => {
    // Validate all links
    const errors = {};
    let hasErrors = false;
    
    Object.entries(socialLinks).forEach(([platform, url]) => {
      if (url && !validateUrl(url, platform)) {
        errors[platform] = 'Please enter a valid URL';
        hasErrors = true;
      }
    });
    
    if (hasErrors) {
      setSocialErrors(errors);
      toast.error('Please fix the invalid URLs');
      return;
    }
    
    try {
      setSaving(true);
      await api.put(`/users/${user.id}`, socialLinks);
      if (refreshUser) await refreshUser();
      toast.success('Social links updated');
    } catch (err) {
      toast.error('Failed to update social links');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await api.put('/settings', settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="settings-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="animate-in max-w-3xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-serif text-2xl mb-2">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account preferences and privacy</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-7 h-auto p-1 bg-mist rounded-sm">
          <TabsTrigger value="profile" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-profile">
            <User size={14} className="mr-1.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="membership" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-membership">
            <Crown size={14} className="mr-1.5" /> Membership
          </TabsTrigger>
          <TabsTrigger value="social" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-social">
            <Share2 size={14} className="mr-1.5" /> Social
          </TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-notifications">
            <Bell size={14} className="mr-1.5" /> Alerts
          </TabsTrigger>
          <TabsTrigger value="privacy" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-privacy">
            <Shield size={14} className="mr-1.5" /> Privacy
          </TabsTrigger>
          <TabsTrigger value="ai" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-ai">
            <Bot size={14} className="mr-1.5" /> AI
          </TabsTrigger>
          <TabsTrigger value="language" className="text-xs py-2 data-[state=active]:bg-obsidian data-[state=active]:text-white rounded-sm" data-testid="tab-language">
            <Globe size={14} className="mr-1.5" /> Language
          </TabsTrigger>
        </TabsList>

        {/* Membership Tab */}
        <TabsContent value="membership">
          <Card className="border-border/40 rounded-sm" data-testid="membership-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif flex items-center gap-2">
                <Crown size={18} className="text-amber-600" /> Your Membership
              </CardTitle>
              <CardDescription>Subscription plan, billing status, and AI tool access</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="flex items-center justify-between p-4 rounded-xl border border-border/40 bg-gradient-to-r from-amber-50/50 to-white" data-testid="membership-status">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${user?.subscription_status === 'active' ? 'bg-emerald-500' : user?.subscription_status === 'trial' ? 'bg-amber-400' : 'bg-red-400'}`} />
                  <div>
                    <p className="font-medium text-sm capitalize">
                      {user?.role === 'admin' ? 'Platform Owner — Full Access'
                        : user?.subscription_status === 'active'
                        ? `${user?.subscription_plan === 'studio' ? 'Full AI Studio' : user?.subscription_plan === 'creator' ? 'Creator AI' : 'Marketplace'} — Active`
                        : user?.subscription_status === 'trial' ? 'Free Trial — Active' : 'Membership Expired'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user?.subscription_status === 'trial' && user?.trial_end_date
                        ? `Trial ends ${new Date(user.trial_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                        : user?.subscription_status === 'active' && user?.subscription_end_date
                        ? `Renews ${new Date(user.subscription_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                        : 'Subscribe to regain access'}
                    </p>
                  </div>
                </div>
                {user?.subscription_status === 'active' && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium">Active</span>
                )}
              </div>

              {/* Three Tier Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Marketplace Tier */}
                <div className={`border rounded-xl p-5 ${(user?.subscription_plan === 'marketplace' || user?.subscription_plan === 'monthly') && user?.subscription_status === 'active' ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white'}`} data-testid="tier-marketplace">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown size={16} className="text-amber-600" />
                    <span className="font-serif text-base">Marketplace</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-serif text-2xl text-obsidian">{'\u20AC'}29</span>
                    <span className="text-xs text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {['Community & messaging', 'Marketplace access', 'Sell products', 'Creator profile', 'Business dashboard'].map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-[11px] text-obsidian/70">
                        <CheckCircle2 size={12} className="text-amber-500 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {(user?.subscription_plan === 'marketplace' || user?.subscription_plan === 'monthly') && user?.subscription_status === 'active' ? (
                    <div className="text-center text-[11px] text-emerald-700 bg-emerald-100 rounded-full py-2 font-medium">
                      <CheckCircle2 size={12} className="inline mr-1" /> Current Plan
                    </div>
                  ) : user?.role !== 'admin' && user?.subscription_status !== 'active' ? (
                    <a href={`https://buy.stripe.com/cNidRbfENgRR113fSk8AE00?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`} target="_blank" rel="noopener noreferrer">
                      <Button data-testid="subscribe-marketplace-btn" className="w-full rounded-full h-9 bg-obsidian hover:bg-amber-600 transition-colors text-[10px] uppercase tracking-wider">
                        Subscribe — {'\u20AC'}29/month
                      </Button>
                    </a>
                  ) : null}
                </div>

                {/* Creator AI Tier */}
                <div className={`border rounded-xl p-5 ${user?.subscription_plan === 'creator' && user?.subscription_status === 'active' ? 'border-emerald-300 bg-emerald-50/30' : 'border-violet-200/60 bg-gradient-to-br from-violet-50/30 to-white'}`} data-testid="tier-creator">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={16} className="text-violet-600" />
                    <span className="font-serif text-base">Creator AI</span>
                    <span className="text-[9px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-bold uppercase">Popular</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-serif text-2xl text-obsidian">{'\u20AC'}49</span>
                    <span className="text-xs text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {['Everything in Marketplace', 'AI Product Creator', 'AI images (3/day)', 'eBooks, planners, toolkits', 'AI wallpaper generation'].map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-[11px] text-obsidian/70">
                        <CheckCircle2 size={12} className="text-violet-500 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {user?.subscription_plan === 'creator' && user?.subscription_status === 'active' ? (
                    <div className="text-center text-[11px] text-emerald-700 bg-emerald-100 rounded-full py-2 font-medium">
                      <CheckCircle2 size={12} className="inline mr-1" /> Current Plan
                    </div>
                  ) : user?.role !== 'admin' ? (
                    <a href={`https://buy.stripe.com/eVq7sN3W51WX5hj0Xq8AE01?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`} target="_blank" rel="noopener noreferrer">
                      <Button data-testid="subscribe-creator-btn" className="w-full rounded-full h-9 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-[10px] uppercase tracking-wider">
                        Subscribe — {'\u20AC'}49/month
                      </Button>
                    </a>
                  ) : null}
                </div>

                {/* Studio AI Tier */}
                <div className={`border rounded-xl p-5 ${user?.subscription_plan === 'studio' && user?.subscription_status === 'active' ? 'border-emerald-300 bg-emerald-50/30' : 'border-rose-200/60 bg-gradient-to-br from-rose-50/30 to-white'}`} data-testid="tier-studio">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap size={16} className="text-rose-600" />
                    <span className="font-serif text-base">Full AI Studio</span>
                    <span className="text-[9px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-bold uppercase">Pro</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-serif text-2xl text-obsidian">{'\u20AC'}100</span>
                    <span className="text-xs text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {['Everything in Creator AI', 'AI video generation (2/day)', 'AI audio / voice studio', 'Bulk product generation', 'Priority processing'].map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-[11px] text-obsidian/70">
                        <CheckCircle2 size={12} className="text-rose-500 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {user?.subscription_plan === 'studio' && user?.subscription_status === 'active' ? (
                    <div className="text-center text-[11px] text-emerald-700 bg-emerald-100 rounded-full py-2 font-medium">
                      <CheckCircle2 size={12} className="inline mr-1" /> Current Plan
                    </div>
                  ) : user?.role !== 'admin' ? (
                    <a href={`https://buy.stripe.com/9B600l2S1dFF9xz49C8AE02?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`} target="_blank" rel="noopener noreferrer">
                      <Button data-testid="subscribe-studio-btn" className="w-full rounded-full h-9 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white text-[10px] uppercase tracking-wider">
                        Subscribe — {'\u20AC'}100/month
                      </Button>
                    </a>
                  ) : null}
                </div>
              </div>

              {user?.role === 'admin' && (
                <div className="text-center text-xs text-muted-foreground p-3 bg-blue-50 rounded-lg">
                  <Shield size={16} className="inline mr-1 text-blue-500" /> Admin accounts have full access to all features.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-border/40 rounded-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif text-lg">Profile Information</CardTitle>
              <CardDescription className="text-xs">Update your public profile details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Display Name</Label>
                <Input
                  data-testid="input-display-name"
                  value={profile.display_name}
                  onChange={e => setProfile({ ...profile, display_name: e.target.value })}
                  className="rounded-sm text-sm h-10"
                  placeholder="Your display name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Bio</Label>
                <Textarea
                  data-testid="input-bio"
                  value={profile.bio}
                  onChange={e => setProfile({ ...profile, bio: e.target.value })}
                  className="rounded-sm text-sm min-h-[100px] resize-none"
                  placeholder="Tell others about yourself..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium">Avatar URL</Label>
                <Input
                  data-testid="input-avatar"
                  value={profile.avatar}
                  onChange={e => setProfile({ ...profile, avatar: e.target.value })}
                  className="rounded-sm text-sm h-10"
                  placeholder="https://..."
                />
              </div>
              <Button
                data-testid="save-profile-btn"
                onClick={saveProfile}
                disabled={saving}
                className="rounded-sm text-xs uppercase tracking-wider h-10 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Save Profile
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Links Tab */}
        <TabsContent value="social">
          <Card className="border-border/40 rounded-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif text-lg">Social Connections</CardTitle>
              <CardDescription className="text-xs">Add your social media links to display on your profile</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Facebook */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <Facebook size={16} className="text-blue-600" /> Facebook URL
                </Label>
                <Input
                  data-testid="input-social-facebook"
                  value={socialLinks.social_facebook}
                  onChange={e => handleSocialLinkChange('social_facebook', e.target.value)}
                  className={`rounded-sm text-sm h-10 ${socialErrors.social_facebook ? 'border-red-500' : ''}`}
                  placeholder="https://facebook.com/yourprofile"
                />
                {socialErrors.social_facebook && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {socialErrors.social_facebook}
                  </p>
                )}
              </div>

              {/* Instagram */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <Instagram size={16} className="text-pink-600" /> Instagram URL
                </Label>
                <Input
                  data-testid="input-social-instagram"
                  value={socialLinks.social_instagram}
                  onChange={e => handleSocialLinkChange('social_instagram', e.target.value)}
                  className={`rounded-sm text-sm h-10 ${socialErrors.social_instagram ? 'border-red-500' : ''}`}
                  placeholder="https://instagram.com/yourprofile"
                />
                {socialErrors.social_instagram && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {socialErrors.social_instagram}
                  </p>
                )}
              </div>

              {/* TikTok */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <TikTokIcon size={16} /> TikTok URL
                </Label>
                <Input
                  data-testid="input-social-tiktok"
                  value={socialLinks.social_tiktok}
                  onChange={e => handleSocialLinkChange('social_tiktok', e.target.value)}
                  className={`rounded-sm text-sm h-10 ${socialErrors.social_tiktok ? 'border-red-500' : ''}`}
                  placeholder="https://tiktok.com/@yourprofile"
                />
                {socialErrors.social_tiktok && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {socialErrors.social_tiktok}
                  </p>
                )}
              </div>

              {/* YouTube */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <Youtube size={16} className="text-red-600" /> YouTube URL
                </Label>
                <Input
                  data-testid="input-social-youtube"
                  value={socialLinks.social_youtube}
                  onChange={e => handleSocialLinkChange('social_youtube', e.target.value)}
                  className={`rounded-sm text-sm h-10 ${socialErrors.social_youtube ? 'border-red-500' : ''}`}
                  placeholder="https://youtube.com/@yourchannel"
                />
                {socialErrors.social_youtube && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {socialErrors.social_youtube}
                  </p>
                )}
              </div>

              {/* Pinterest */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <PinterestIcon size={16} className="text-red-700" /> Pinterest URL
                </Label>
                <Input
                  data-testid="input-social-pinterest"
                  value={socialLinks.social_pinterest}
                  onChange={e => handleSocialLinkChange('social_pinterest', e.target.value)}
                  className={`rounded-sm text-sm h-10 ${socialErrors.social_pinterest ? 'border-red-500' : ''}`}
                  placeholder="https://pinterest.com/yourprofile"
                />
                {socialErrors.social_pinterest && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {socialErrors.social_pinterest}
                  </p>
                )}
              </div>

              {/* Info box */}
              <div className="bg-mist p-4 rounded-sm mt-4">
                <h4 className="text-xs font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-600" /> How it works
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Links will display as icons on your public profile</li>
                  <li>• Leave a field empty to hide that platform</li>
                  <li>• All links open in a new browser tab</li>
                  <li>• Your social links are visible to all visitors</li>
                </ul>
              </div>

              <Button
                data-testid="save-social-btn"
                onClick={saveSocialLinks}
                disabled={saving}
                className="rounded-sm text-xs uppercase tracking-wider h-10 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Save Social Links
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="border-border/40 rounded-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif text-lg">Notification Preferences</CardTitle>
              <CardDescription className="text-xs">Choose what you want to be notified about</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive notifications via email</p>
                </div>
                <Switch
                  data-testid="switch-email"
                  checked={settings.notification_email}
                  onCheckedChange={v => setSettings({ ...settings, notification_email: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Push Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive push notifications in-app</p>
                </div>
                <Switch
                  data-testid="switch-push"
                  checked={settings.notification_push}
                  onCheckedChange={v => setSettings({ ...settings, notification_push: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">New Messages</Label>
                  <p className="text-xs text-muted-foreground">When someone sends you a message</p>
                </div>
                <Switch
                  data-testid="switch-messages"
                  checked={settings.notification_messages}
                  onCheckedChange={v => setSettings({ ...settings, notification_messages: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Likes</Label>
                  <p className="text-xs text-muted-foreground">When someone likes your content</p>
                </div>
                <Switch
                  data-testid="switch-likes"
                  checked={settings.notification_likes}
                  onCheckedChange={v => setSettings({ ...settings, notification_likes: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Comments</Label>
                  <p className="text-xs text-muted-foreground">When someone comments on your posts</p>
                </div>
                <Switch
                  data-testid="switch-comments"
                  checked={settings.notification_comments}
                  onCheckedChange={v => setSettings({ ...settings, notification_comments: v })}
                />
              </div>
              <Button
                data-testid="save-notifications-btn"
                onClick={saveSettings}
                disabled={saving}
                className="rounded-sm text-xs uppercase tracking-wider h-10 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Privacy Tab */}
        <TabsContent value="privacy">
          <Card className="border-border/40 rounded-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif text-lg">Privacy Settings</CardTitle>
              <CardDescription className="text-xs">Control your visibility and data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Public Profile</Label>
                  <p className="text-xs text-muted-foreground">Allow others to view your profile</p>
                </div>
                <Switch
                  data-testid="switch-profile-visible"
                  checked={settings.privacy_profile_visible}
                  onCheckedChange={v => setSettings({ ...settings, privacy_profile_visible: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Show Online Status</Label>
                  <p className="text-xs text-muted-foreground">Let others see when you're online</p>
                </div>
                <Switch
                  data-testid="switch-online-status"
                  checked={settings.privacy_online_status}
                  onCheckedChange={v => setSettings({ ...settings, privacy_online_status: v })}
                />
              </div>
              <Button
                data-testid="save-privacy-btn"
                onClick={saveSettings}
                disabled={saving}
                className="rounded-sm text-xs uppercase tracking-wider h-10 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Save Privacy Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Assistant Tab */}
        <TabsContent value="ai">
          <Card className="border-border/40 rounded-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif text-lg">AI Messaging Assistant</CardTitle>
              <CardDescription className="text-xs">Configure AI-powered message features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="text-sm font-medium">AI Reply Mode</Label>
                <Select
                  value={settings.ai_reply_mode}
                  onValueChange={v => setSettings({ ...settings, ai_reply_mode: v })}
                >
                  <SelectTrigger className="rounded-sm text-sm h-10" data-testid="select-ai-mode">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off - No AI assistance</SelectItem>
                    <SelectItem value="auto_reply">Auto-Reply - Send automatic replies when away</SelectItem>
                    <SelectItem value="suggestions">Suggestions - Get AI reply suggestions</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {settings.ai_reply_mode === 'off' && "AI messaging features are disabled"}
                  {settings.ai_reply_mode === 'auto_reply' && "When you're inactive for 5+ minutes, AI will send your auto-reply message"}
                  {settings.ai_reply_mode === 'suggestions' && "Get AI-powered reply suggestions while chatting"}
                </p>
              </div>

              {settings.ai_reply_mode === 'auto_reply' && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Auto-Reply Message</Label>
                  <Textarea
                    data-testid="input-auto-reply"
                    value={settings.ai_auto_reply_message}
                    onChange={e => setSettings({ ...settings, ai_auto_reply_message: e.target.value })}
                    className="rounded-sm text-sm min-h-[80px] resize-none"
                    placeholder="Your automatic reply message..."
                  />
                  <p className="text-xs text-muted-foreground">This message will be sent automatically when you're away</p>
                </div>
              )}

              <div className="bg-mist p-4 rounded-sm">
                <h4 className="text-xs font-medium mb-2 flex items-center gap-2">
                  <Bot size={14} className="text-gold" /> How it works
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• <strong>Auto-Reply:</strong> Automatically responds when you haven't been active for 5 minutes</li>
                  <li>• <strong>Suggestions:</strong> Click the AI button while chatting to get smart reply options</li>
                  <li>• All AI features use the Susan David Community assistant</li>
                </ul>
              </div>

              <Button
                data-testid="save-ai-btn"
                onClick={saveSettings}
                disabled={saving}
                className="rounded-sm text-xs uppercase tracking-wider h-10 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Save AI Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language">
          <Card className="border-border/40 rounded-sm" data-testid="language-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-serif flex items-center gap-2">
                <Globe size={18} className="text-amber-600" /> Language, Currency &amp; Region
              </CardTitle>
              <CardDescription>Pick your preferred language and the currency you want to see marketplace prices in. Both choices are remembered across devices.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50/40 border border-amber-100">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-amber-700 font-semibold mb-1">Language</p>
                  <p className="text-sm text-obsidian/70">Interface and message translations — 16 languages supported.</p>
                </div>
                <LanguageSwitcher variant="full" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50/40 border border-amber-100">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-amber-700 font-semibold mb-1">Currency</p>
                  <p className="text-sm text-obsidian/70">Marketplace prices are converted live using ECB exchange rates.</p>
                </div>
                <CurrencySwitcher variant="full" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Supported languages: English, Deutsch, Francais, Espanol, Portugues, Italiano, Nederlands, Chinese, Japanese, Korean, Arabic, Hindi, Turkce, Russian, Polski, Svenska.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}


